import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useLanguage, localizedPath } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { ART_LABEL } from "@/lib/categories";
import { Slideshow } from "@/components/Slideshow";

// Slideshow über eine ganze Rubrik (gemischte Bilder aller Serien der Rubrik).
// Einstieg von /projekte per ?bild=N. Layout und Bedienung stecken in der
// geteilten Komponente Slideshow (wie auf den Serien-Seiten).
export default function CategorySlideshow() {
  const { art } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sections, setSections] = useState(null);
  const [index, setIndex] = useState(0);
  const { t, lang } = useLanguage();
  useSeo({
    title: `${t(ART_LABEL[art] || art)} · Wim Jansen Delgado · Fotograf Hamburg`,
    description: `${t(ART_LABEL[art] || art)}, Fotografie von Wim Jansen Delgado, Hamburg.`,
    robots: "noindex, follow",
  });

  useEffect(() => {
    api
      .get("/overview-grid")
      .then((res) => setSections(res.data || []))
      .catch(() => setSections([]));
  }, []);

  const images = useMemo(() => sections?.find((s) => s.art === art)?.images || [], [sections, art]);

  // Fokus-Modus: Kopf- und Fußzeile ausblenden, solange die Slideshow offen ist
  useEffect(() => {
    document.body.classList.add("focus-slideshow");
    return () => document.body.classList.remove("focus-slideshow");
  }, []);

  // Direkteinstieg: ?bild=5 öffnet das 5. Bild der Rubrik
  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (deepLinkDone.current || images.length === 0) return;
    deepLinkDone.current = true;
    const b = parseInt(searchParams.get("bild") || "", 10);
    if (b >= 1 && b <= images.length) setIndex(b - 1);
  }, [images, searchParams]);

  if (sections === null) {
    return (
      <div data-testid="category-slideshow" className="mx-auto max-w-7xl px-4 md:px-12 lg:px-16">
        <div className="flex min-h-[100dvh] items-center justify-center">
          <div className="h-[50vh] w-full max-w-3xl animate-pulse bg-panel" />
        </div>
      </div>
    );
  }
  if (images.length === 0) {
    return <Navigate to={localizedPath("/projekte", lang)} replace />;
  }

  const slides = images.map((img) => ({    key: img.image,
    image: img.image,
    title: img.title || t(ART_LABEL[art] || art),
    ort: lang === "en" ? img.ort_en || "" : img.ort || "",
    alt: (lang === "en" ? img.alt_en : img.alt_de) || img.title || t("Bild"),
    orientation: img.orientation,
  }));

  // Zurück zur Übersicht: Rubrik geöffnet, zur zuletzt gesehenen Kachel
  const backToOverview = () =>
    navigate(`${localizedPath("/projekte", lang)}?rubrik=${art}&bild=${index + 1}`);

  return (
    <div data-testid="category-slideshow">
      <section className="relative mx-auto flex min-h-[100dvh] max-w-7xl flex-col justify-center px-4 pb-8 pt-6 md:px-12 lg:px-16">
        <Slideshow
          images={slides}
          index={index}
          onIndexChange={setIndex}
          onOverview={backToOverview}
          overviewLabel={t("Übersicht")}
        />
      </section>
    </div>
  );
}
